import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import { formatPayloadIfNeeded } from "./utils";
import {
  downloadFile,
  generateAuditObj,
  getItem,
  showToast,
} from "services/utils";
import { getMasterDocDataByKey } from "services/ReduxUtils";
import { Constants } from "services/keyConstants";

const DEFAULT_RETRY = 1;

export function createCRUDService({ baseUrl, queryKey, custom = {} }) {
  return {
    list: (params = {}, options = {}) => {
      return useQuery({
        queryKey: [queryKey, params],
        queryFn: () => axios.get(baseUrl, { params }).then((res) => res.data),
        ...options,
        retry: options.retry ?? DEFAULT_RETRY,
        refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
      });
    },
    count: (options = {}) =>
      useQuery({
        queryKey: [`${queryKey}-count`],
        queryFn: () =>
          axios.get(`${baseUrl}/get-count`).then((res) => res.data),
        retry: options.retry ?? DEFAULT_RETRY,
      }),

    getById: (id, options = {}) =>
      useQuery({
        queryKey: [queryKey, id],
        queryFn: () => axios.get(`${baseUrl}/${id}`).then((res) => res.data),
        enabled: !!id,
        retry: options.retry ?? DEFAULT_RETRY,
        refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
        refetchOnMount: options.refetchOnMount ?? false,
      }),

    create: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false
          );
          return axios
            .post(`${baseUrl}/add`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          queryClient.invalidateQueries([queryKey]);
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },

    download: () => {
      const queryClient = useQueryClient();

      return useMutation({
        mutationFn: async (data) => {
          const { payloadObj, onChangeDownloadState } = data ?? {};
          let finalObj = { ...payloadObj };
          showToast("File started to download", true);

          // Build audit object if auditData is present
          if (finalObj?.auditData) {
            let userData = getItem(Constants.KEY_USER_DATA),
              selectedRole = getItem(Constants.KEY_SELECTED_ROLE);

            let defaultMasterDocData = getMasterDocDataByKey({
              masterDocId: finalObj?.auditData?.masterDocId,
            });

            finalObj.auditObj = generateAuditObj({
              documentId: finalObj?.documentId || finalObj?.formatId,
              changedFields: [],
              documentType: AuditDocumentTypes.Record,
              documentName: defaultMasterDocData?.name,
              action: AuditActionTypeConstant.Download,
              actionMessage: `${userData?.name}-${selectedRole?.roleName} Download ${defaultMasterDocData?.name}`,
              masterDocId: defaultMasterDocData?.masterDocId,
              locationPath: finalObj?.navigateUrl,
            });

            delete finalObj?.auditData;
          }

          try {
            // Set the download state to true
            onChangeDownloadState?.(true);

            const data = formatPayloadIfNeeded(
              finalObj,
              finalObj?.isCustomImage ?? false
            );

            // API call
            const result = await axios
              .post(`${baseUrl}/download`, data, {
                headers:
                  data instanceof FormData
                    ? { "Content-Type": "multipart/form-data" }
                    : {},
              })
              .then((res) => res.data);

            // If successful, trigger file download
            if (result?.meta?.success) {
              await downloadFile(result?.data);
            }

            return result;
          } catch (error) {
            showToast("Error during download", false);
            throw error;
          } finally {
            onChangeDownloadState?.(false);
          }
        },
        onSuccess: (data) => {
          queryClient.invalidateQueries([queryKey]);
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },

    update: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: ({ id, data: payload }) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false
          );
          return axios
            .put(`${baseUrl}/${id}`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          queryClient.invalidateQueries([queryKey]);
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },

    updateSpecific: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: ({ data: payload }) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false
          );
          return axios
            .put(`${baseUrl}/update-specific`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          queryClient.invalidateQueries([queryKey]);
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },

    delete: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: (id) =>
          axios.delete(`${baseUrl}/${id}`).then((res) => res.data),
        onSuccess: (data) => {
          queryClient.invalidateQueries([queryKey]);
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },

    ...custom,
  };
}
